/**
 * Neue Person anlegen – eigenständige Tab-Seite
 * 2-Spalten-Layout: Formular links, Dokumentenvorschau rechts
 * KI-Autofill: Extrahiert Personendaten aus dem Dokument-OCR-Text
 */
import { useState, useEffect, useCallback } from 'react';
import { UserPlus, Check, Loader2, X, Sparkles, FileText, Eye, AlertCircle, Search, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitEntityCreated, ENTITY_TYPES } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const FIELD_KEYS = ['vorname', 'nachname', 'geburtsdatum', 'strasse', 'hausnummer', 'plz', 'ort', 'telefon', 'email', 'notiz'];

/** Farbmarkierung für KI-befüllte Felder */
function fieldStyle(fieldKey, kiStatus) {
  if (!kiStatus) return '';
  if (kiStatus[fieldKey] === 'found') return 'ring-2 ring-orange-400 bg-orange-50';
  if (kiStatus[fieldKey] === 'not_found') return 'ring-2 ring-red-300 bg-red-50';
  return '';
}

/** Mini-Dokumentenvorschau */
function DokumentMiniVorschau({ url, name, dateityp }) {
  if (!url) return null;

  const isPdf = dateityp?.toLowerCase().endsWith('.pdf') || name?.toLowerCase().endsWith('.pdf');
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(name || '') || /\.(jpg|jpeg|png|gif|webp)$/i.test(dateityp || '');

  return (
    <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="p-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-xs font-medium text-slate-600 flex items-center gap-1.5 truncate">
          <Eye className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate">{name || 'Dokument'}</span>
        </p>
        <Button
          size="sm" variant="ghost" className="h-6 px-2 text-xs"
          onClick={() => window.open(url, '_blank')}
        >
          <Eye className="w-3 h-3 mr-1" /> Groß
        </Button>
      </div>
      <div className="flex-1 overflow-hidden bg-slate-100 min-h-[300px]">
        {isPdf ? (
          <object data={url} type="application/pdf" className="w-full h-full border-0" style={{ minHeight: '100%' }}>
            <iframe src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
              className="w-full h-full border-0" title="Vorschau" />
          </object>
        ) : isImage ? (
          <div className="w-full h-full flex items-center justify-center p-2">
            <img src={url} alt="Vorschau" className="max-w-full max-h-full object-contain rounded" />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            <div className="text-center">
              <FileText className="w-8 h-8 mx-auto mb-1" />
              <p className="text-xs">Vorschau nicht verfügbar</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function NeuePersonPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};

  const [form, setForm] = useState({
    vorname: '', nachname: '', geburtsdatum: '',
    strasse: '', hausnummer: '', plz: '', ort: '',
    telefon: '', email: '', notiz: ''
  });
  const [saving, setSaving] = useState(false);
  const [kiLoading, setKiLoading] = useState(false);
  const [kiStatus, setKiStatus] = useState(null); // { feldname: 'found' | 'not_found' }
  const [kiStats, setKiStats] = useState(null); // { anzahl_gefunden, anzahl_nicht_gefunden }
  const [webSearchLoading, setWebSearchLoading] = useState(false);
  const [webSearchResult, setWebSearchResult] = useState(null);

  const hasDocument = !!params.dokumentVorschauUrl;
  const hasOcrText = !!(params.ocrText && params.ocrText.length > 10);
  const hasDokumentId = !!params.dokumentId;
  const canUseKi = hasOcrText || hasDokumentId;

  useEffect(() => {
    if (params.vorname || params.nachname) {
      setForm(prev => ({
        ...prev,
        vorname: params.vorname || prev.vorname,
        nachname: params.nachname || prev.nachname,
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** KI-Autofill: Extrahiert Daten aus OCR-Text */
  const handleKiAutofill = async () => {
    if (!canUseKi) {
      toast.error('Kein Dokument oder OCR-Text vorhanden');
      return;
    }
    if (!form.nachname.trim()) {
      toast.error('Bitte zuerst mindestens den Nachnamen eingeben');
      return;
    }

    setKiLoading(true);
    setKiStatus(null);
    setKiStats(null);

    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/extract-person-data`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ocr_text: params.ocrText || null,
          dokument_id: params.dokumentId || null,
          vorname: form.vorname || null,
          nachname: form.nachname,
        }),
      });
      const data = await res.json();

      if (data.success && data.daten) {
        // Formular mit gefundenen Daten füllen (nur wenn Feld leer oder KI hat Wert)
        setForm(prev => {
          const next = { ...prev };
          for (const key of FIELD_KEYS) {
            if (data.daten[key] && data.felder_status[key] === 'found') {
              next[key] = data.daten[key];
            }
          }
          return next;
        });
        setKiStatus(data.felder_status);
        setKiStats({ gefunden: data.anzahl_gefunden, nicht_gefunden: data.anzahl_nicht_gefunden });
        toast.success(`Zeo: ${data.anzahl_gefunden} Felder gefunden, ${data.anzahl_nicht_gefunden} nicht gefunden`);
      } else {
        toast.error(data.error || 'KI-Extraktion fehlgeschlagen');
      }
    } catch (e) {
      console.error('KI-Autofill Fehler:', e);
      toast.error('Fehler bei der KI-Analyse');
    } finally {
      setKiLoading(false);
    }
  };

  /** Feld-Eingabe: Setzt KI-Status für das Feld zurück */
  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (kiStatus?.[key]) {
      setKiStatus(prev => ({ ...prev, [key]: undefined }));
    }
  };

  /** KI-Websuche: Sucht nach Kontaktdaten einer Institution/Person */
  const handleWebSearch = async () => {
    const searchName = `${form.vorname} ${form.nachname}`.trim();
    if (!searchName || searchName.length < 2) {
      toast.error('Bitte mindestens einen Namen eingeben');
      return;
    }
    setWebSearchLoading(true);
    setWebSearchResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wiki/recherche-institution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: searchName }),
      });
      const data = await res.json();
      if (data.success && data.ergebnis) {
        const e = data.ergebnis;
        setWebSearchResult(e);
        // Formular mit gefundenen Daten fuellen (nur leere Felder)
        setForm(prev => ({
          ...prev,
          strasse: prev.strasse || e.adresse || '',
          plz: prev.plz || e.plz || '',
          ort: prev.ort || e.ort || '',
          telefon: prev.telefon || e.telefon || '',
          email: prev.email || e.email || '',
          notiz: prev.notiz || (e.website ? `Website: ${e.website}` : '') || '',
        }));
        // KI-Status fuer befuellte Felder setzen
        const newStatus = {};
        if (e.adresse) newStatus.strasse = 'found';
        if (e.plz) newStatus.plz = 'found';
        if (e.ort) newStatus.ort = 'found';
        if (e.telefon) newStatus.telefon = 'found';
        if (e.email) newStatus.email = 'found';
        setKiStatus(prev => ({ ...(prev || {}), ...newStatus }));
        const found = Object.values(newStatus).filter(v => v === 'found').length;
        toast.success(`KI-Recherche: ${found} Felder gefuellt`);
      } else {
        toast.error(data.message || 'Keine Daten gefunden');
      }
    } catch (err) {
      console.error('Websuche-Fehler:', err);
      toast.error('Fehler bei der KI-Recherche');
    } finally {
      setWebSearchLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.nachname.trim()) {
      toast.error('Nachname ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      const result = await betreuungAPI.createKlient({
        vorname: form.vorname || null,
        nachname: form.nachname,
        geburtsdatum: form.geburtsdatum || null,
        strasse: form.strasse || null,
        hausnummer: form.hausnummer || null,
        plz: form.plz || null,
        ort: form.ort || null,
        telefon: form.telefon || null,
        email: form.email || null,
        notizen: form.notiz || null,
        status: 'aktiv'
      });
      toast.success(`Person "${form.vorname} ${form.nachname}" angelegt`);
      emitEntityCreated(ENTITY_TYPES.PERSON, result);
      closeTab(activeTab.id);
    } catch (e) {
      console.error('Fehler:', e);
      toast.error('Fehler beim Anlegen');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = useCallback(() => {
    closeTab(activeTab.id);
  }, [closeTab, activeTab?.id]);

  return (
    <div className={`flex gap-4 ${hasDocument ? 'max-w-5xl' : 'max-w-xl'} mx-auto`} data-testid="neue-person-page">
      {/* Linke Spalte: Formular */}
      <div className={hasDocument ? 'flex-1 min-w-0' : 'w-full'}>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <UserPlus className="w-5 h-5 text-green-600" />
                Neue Person anlegen
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={handleClose} data-testid="close-neue-person">
                <X className="w-4 h-4" />
              </Button>
            </div>
            <CardDescription>Erfassen Sie die Daten der neuen Person. Pflichtfeld: Nachname</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* KI-Autofill Button */}
            {canUseKi && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg bg-indigo-50 border border-indigo-100">
                <Sparkles className="w-4 h-4 text-indigo-500 shrink-0" />
                <p className="text-xs text-indigo-700 flex-1">
                  Zeo kann Daten aus dem Dokument extrahieren. Geben Sie Vor-/Nachname ein und klicken Sie auf Autofill.
                </p>
                <Button
                  size="sm" variant="outline"
                  onClick={handleKiAutofill}
                  disabled={kiLoading || !form.nachname.trim()}
                  className="shrink-0 border-indigo-200 text-indigo-700 hover:bg-indigo-100"
                  data-testid="ki-autofill-btn"
                >
                  {kiLoading ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Analysiere...</>
                  ) : (
                    <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> KI-Autofill</>
                  )}
                </Button>
              </div>
            )}

            {/* KI-Websuche Button */}
            <div className="flex items-center gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
              <Globe className="w-4 h-4 text-emerald-500 shrink-0" />
              <p className="text-xs text-emerald-700 flex-1">
                Adresse, Telefon und E-Mail einer Institution per KI-Recherche automatisch fuellen.
              </p>
              <Button
                size="sm" variant="outline"
                onClick={handleWebSearch}
                disabled={webSearchLoading || (!form.vorname.trim() && !form.nachname.trim())}
                className="shrink-0 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                data-testid="ki-websuche-btn"
              >
                {webSearchLoading ? (
                  <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Suche...</>
                ) : (
                  <><Search className="w-3.5 h-3.5 mr-1.5" /> KI-Recherche</>
                )}
              </Button>
            </div>

            {/* KI-Status Legende */}
            {kiStatus && kiStats && (
              <div className="flex items-center gap-4 text-xs px-1">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-orange-400" />
                  <span className="text-slate-600">Von KI gefunden ({kiStats.gefunden})</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-sm bg-red-300" />
                  <span className="text-slate-600">Nicht gefunden ({kiStats.nicht_gefunden})</span>
                </span>
              </div>
            )}

            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="np-vorname">Vorname</Label>
                <Input id="np-vorname" value={form.vorname}
                  onChange={e => updateField('vorname', e.target.value)}
                  placeholder="Max" data-testid="person-vorname-input"
                  className={fieldStyle('vorname', kiStatus)} />
              </div>
              <div>
                <Label htmlFor="np-nachname">Nachname *</Label>
                <Input id="np-nachname" value={form.nachname}
                  onChange={e => updateField('nachname', e.target.value)}
                  placeholder="Mustermann" required data-testid="person-nachname-input"
                  className={fieldStyle('nachname', kiStatus)} />
              </div>
            </div>

            {/* Geburtsdatum */}
            <div>
              <Label htmlFor="np-geburtsdatum">Geburtsdatum</Label>
              <Input id="np-geburtsdatum" type="date" value={form.geburtsdatum}
                onChange={e => updateField('geburtsdatum', e.target.value)}
                className={fieldStyle('geburtsdatum', kiStatus)} />
            </div>

            {/* Adresse */}
            <div className="space-y-2">
              <Label>Adresse</Label>
              <div className="grid grid-cols-4 gap-2">
                <Input className={`col-span-3 ${fieldStyle('strasse', kiStatus)}`} value={form.strasse}
                  onChange={e => updateField('strasse', e.target.value)} placeholder="Strasse" />
                <Input value={form.hausnummer} className={fieldStyle('hausnummer', kiStatus)}
                  onChange={e => updateField('hausnummer', e.target.value)} placeholder="Nr." />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Input value={form.plz} className={fieldStyle('plz', kiStatus)}
                  onChange={e => updateField('plz', e.target.value)} placeholder="PLZ" />
                <Input className={`col-span-2 ${fieldStyle('ort', kiStatus)}`} value={form.ort}
                  onChange={e => updateField('ort', e.target.value)} placeholder="Ort" />
              </div>
            </div>

            {/* Kontakt */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="np-telefon">Telefon</Label>
                <Input id="np-telefon" value={form.telefon}
                  onChange={e => updateField('telefon', e.target.value)} placeholder="0123 456789"
                  className={fieldStyle('telefon', kiStatus)} />
              </div>
              <div>
                <Label htmlFor="np-email">E-Mail</Label>
                <Input id="np-email" type="email" value={form.email}
                  onChange={e => updateField('email', e.target.value)} placeholder="max@beispiel.de"
                  className={fieldStyle('email', kiStatus)} />
              </div>
            </div>

            {/* Notiz */}
            <div>
              <Label htmlFor="np-notiz">Notiz</Label>
              <Textarea id="np-notiz" value={form.notiz}
                onChange={e => updateField('notiz', e.target.value)}
                placeholder="Weitere Informationen..." rows={2}
                className={fieldStyle('notiz', kiStatus)} />
            </div>

            {/* Aktionen */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={handleClose} data-testid="cancel-neue-person">Abbrechen</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-neue-person">
                {saving
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Speichern...</>
                  : <><Check className="w-4 h-4 mr-2" /> Person anlegen</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rechte Spalte: Dokumentenvorschau */}
      {hasDocument && (
        <div className="w-80 shrink-0" data-testid="neue-person-vorschau">
          <DokumentMiniVorschau
            url={params.dokumentVorschauUrl}
            name={params.dokumentName}
            dateityp={params.dokumentDateityp}
          />
        </div>
      )}
    </div>
  );
}
